import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import * as XLSX from "xlsx";
import { FaAngleLeft } from "react-icons/fa";
import ProfilePic from "../../../assets/images/dummy_profile.png";
import Loader from "../../../components/Loader/Loader";
import "../../../components/common/css/campaignview.scss";
import "../../new/new.scss";
import Logo from "../../../assets/logo/logo-image.svg";
import { FaCopy } from "react-icons/fa";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import { useCountries, bankCountryLabel } from "../../../utils/bankDisplay";
import { maskAccount } from "../../../utils/bankMask";
import { resolvedOrderKind, accountKindReasonText } from "../../../utils/orderAccountKind";
import { COMPANY_CHECK, companyCheckOf, orderConfirmed } from "../../../utils/personFirstAccounts";
import {
  CAMPAIGN_NAME_LABEL,
  ISSUER_LEGAL_NAME_LABEL,
  campaignDisplayName,
  identityExportColumns,
} from "../../Campaign/campaignIdentity";
import {
  describeRefusal,
  markPaidOutcome,
  mismatchSummary,
  mismatchFields,
} from "../../Payments/WireReconciliation/wireReceiptUi";

/**
 * How this order was paid, for the invoice.
 *
 * Reads the order's own campaign_investments.paymentOption. That column is
 * written server-side by paymentRouting.legacyPaymentOption ("Wire Transfer" /
 * "Direct Deposit") and by the AD manual-payment console ("MANUAL_BANK");
 * "E Transfer" is legacy data with no current writer.
 *
 * This replaces a useState("Wire Transfer") whose setter was never called, so
 * the invoice claimed Wire Transfer for every order however it was actually
 * paid, and the other two branches were unreachable.
 *
 * Unknown / null is stated plainly rather than guessed: paymentOption is NULL
 * until the investor reaches the payment step, and an invoice that names the
 * wrong rail is worse than one that names none.
 */
const paymentMethodLabel = (paymentOption) => {
  switch (paymentOption) {
    case "Wire Transfer":
      return "Payment method: wire transfer.";
    case "Direct Deposit":
      return "Payment method: pre-authorized debit from the investor's bank account.";
    case "E Transfer":
      return "Payment method: e-transfer.";
    case "MANUAL_BANK":
      return "Payment method: bank transfer, confirmed manually by an administrator.";
    default:
      return "Payment method: not selected yet.";
  }
};

/**
 * Recording a payment from this page (chain M, 2026-10-07).
 *
 * R8 / D5: PUT admin/update-investment no longer accepts a paymentStatus (409
 * PAYMENT_STATUS_NOT_WRITABLE). A payment is recorded only through the wire
 * receipt rules, POST admin/wire/mark-paid (AD only): the bank reference and
 * the amount and currency the bank ACTUALLY credited, keyed by the admin and
 * never prefilled from the order (D9 settles only an exact credit; a figure
 * copied from the order would make the check assert its own premise). The
 * call is awaited and its answer shown in words (wireReceiptUi, shared with
 * the wire queue): a 4xx refusal by its code, a 202 as recorded but NOT paid.
 * Only a settled credit (200) sends the invoice, as
 * { id, invoice_pdf_url } with no paymentStatus. An order already PAID only
 * re-sends its invoice.
 */
const CREDIT_CURRENCIES = ["CAD", "USD"];

/**
 * A closed order: cancelled (an s.2.9 withdrawal included), expired or failed,
 * the API's orderTransition.isTerminal. Nothing is recorded or re-sent for it
 * from this page (fixer round, 2026-10-08): re-sending the invoice of a
 * withdrawn PAID order told the investor and the issuer that the payment was
 * confirmed, on an order whose refund is pending.
 */
const isClosedOrder = (order) =>
  !!order &&
  (order.isCancelled === true ||
    order.isCancelled === 1 ||
    order.OrderStatus === "FAILED" ||
    order.orderSummaryType === "FAILED" ||
    order.paymentStatus === "FAILED");
// An open order that is already PAID (e.g. settled in Wire reconciliation): its
// status is read-only (a paid credit cannot be reversed in this release) and
// the page offers Send invoice at once (HB-04/05 + Codex P1, 2026-10-16).
const isOpenPaidOrder = (order) => !!order && order.paymentStatus === "PAID" && !isClosedOrder(order);
const CLOSED_ORDER_TEXT =
  "This order is cancelled, withdrawn or expired, so no payment can be recorded for it and its invoice cannot be sent.";

/**
 * Person-first D-5 (contract P7; plan D-5): an ENTITY order placed but not yet
 * confirmed is held until its company passes; a confirmed one keeps collecting
 * and settling, so its payment is recorded here as before (the sender check
 * still applies to it). "Confirmed" is the server's own field, the order's
 * status 1 (orderConfirmed: the API's orderTransition.isCounted, read by
 * confirm's D-5 and by recordSettlement, which refuses an order not confirmed
 * with NOT_CONFIRMED anyway), never paymentStatus.
 *
 * The company check of an unconfirmed ENTITY order is the strict predicate
 * with the placeholder rule (companyCheckOf), fail closed. The admin order
 * detail sends comp_isVerified but neither comp_isIdExpired nor
 * comp_verification_error_message (api getMyInvestmentDetailsByID's user
 * attributes) and no accounts block, so a set flag alone answers NOT_STATED and
 * is refused with its own words, never passed on the flag alone.
 */
const COMPANY_NOT_VERIFIED_DIALOG = {
  title: "Error",
  text: "Please verify the corporate details before updating the payment status.",
};
const COMPANY_NOT_STATED_DIALOG = {
  title: "Company status not stated",
  text:
    "This order is paid from a company account and the investor has not confirmed it yet, so the company must be verified first. " +
    "The order details do not say whether the company's verification has expired or carries a reviewer's message, so it cannot be checked here. " +
    "Nothing was recorded.",
};

/**
 * Amendment A3/A5 (api 234d69d5): the sender (remitter) check before a credit
 * is posted from this page. The order detail carries the server's
 * remitterCheckRequired (common/remitterCheck.js: every unsettled ENTITY order
 * on any rail and every unsettled WIRE order), the same rule the AD payment
 * console follows. This page has no remitter step of its own: the check is
 * recorded on that console (Platform oversight > Payments (bank)).
 *   -> null, or { title, text } to show instead of posting:
 *   - remitterCheckRequired missing or not a boolean: refused (fail closed);
 *   - the check required and no MATCH on record: refused, pointing to the
 *     console. recordSettlement refuses an ENTITY order's manual credit without
 *     one (REMITTER_NOT_CONFIRMED). For an INDIVIDUAL wire the check is the
 *     admin's own workflow step and only the console shows what was recorded,
 *     so a MATCH is asked for here too (the console decides a mismatch there).
 *     An INDIVIDUAL order whose recorded check is a MISMATCH is told to
 *     complete its payment on the console (Codex admin r5 P3): the check is
 *     already on record there, and asking for it again sent the AD in a loop.
 * An order already PAID records no credit and is not gated.
 */
const REMITTER_CHECK_LABELS = { UNCHECKED: "Not checked", MATCH: "Match", MISMATCH: "Mismatch" };
function remitterCreditBlock(order, kind) {
  const required = order ? order.remitterCheckRequired : undefined;
  if (required !== true && required !== false) {
    return {
      title: "Sender check not stated",
      text: "This payment cannot be recorded here because the server did not say whether a sender check is needed. Refresh the page.",
    };
  }
  if (required === false) return null;
  const raw = String(order.wire_remitter_check || "").toUpperCase();
  const check = raw === "MATCH" || raw === "MISMATCH" ? raw : "UNCHECKED";
  if (check === "MATCH") return null;
  if (check === "MISMATCH" && kind === "INDIVIDUAL") {
    return {
      title: "Complete this payment on the console",
      text:
        "This order's sender check (the remitter check) is on record as: Mismatch. This page records a payment only after a match, " +
        "so complete the payment under Platform oversight > Payments (bank), on the Manual payment confirmation screen, " +
        "where the mismatch is shown and the server decides. Nothing was recorded.",
    };
  }
  return {
    title: "Sender check needed first",
    text:
      `This order needs a sender check (the remitter check) before its payment can be recorded, and the check on file is: ${REMITTER_CHECK_LABELS[check]}. ` +
      "Record it under Platform oversight > Payments (bank), on the Manual payment confirmation screen. " +
      "Only a recorded match lets the payment be recorded here. Nothing was recorded.",
  };
}

/**
 * The page belongs to the order the route names (review, 2026-10-25): it is
 * remounted per route id (InvestmentEditRoute below), and nothing is posted
 * (no credit recorded, no invoice uploaded or sent) unless the loaded
 * detail's id is the route's.
 *
 * A submission belongs to the page that started it (review round 3,
 * 2026-10-25): the route check used to run once, before the first await, so
 * a submission for order A that was still in flight when the route moved to
 * B went on after A's page was gone, captured B's invoice (a global document
 * query) and sent it against A. Now, after every await, the submission checks
 * that its page is still mounted and still shows the order it was started for
 * (the route's id and the loaded detail's id both equal to it) before it does
 * anything more: no capture, upload, invoice PUT, dialog, reload or
 * navigation once it is not. The invoice is captured from this page's own
 * element (invoiceRef), never from a document query. An order settled and then
 * left this way gets no invoice and no dialog: it reopens as PAID, with Send
 * invoice offered.
 */
const Viewinvestment = () => {
  const navigate = useNavigate();
  const id = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState();
  const [paymentStatus, setPaymentStatus] = useState("");
  // The bank credit, as the admin reads it off the statement (never prefilled).
  const [bankReference, setBankReference] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [receivedCurrency, setReceivedCurrency] = useState("");
  const countries = useCountries();
  // I1 (corporate Business PAD contract): the bank block follows the order's
  // RESOLVED kind (the API's accountKind and accountKindReason only; never the
  // order's own investmentAccount, never the user's entity_type). An
  // unresolved order (one the server sent no kind for included) shows neither
  // kind's coordinates, and no payment is recorded or invoice sent for it.
  const resolved = resolvedOrderKind(data);
  const kind = resolved.kind;
  // The detail on the page is the route's order (ids compared as text: the
  // route's is a string, the detail's a number). Nothing is posted otherwise.
  const detailIsRoute =
    !!data && data.id !== undefined && data.id !== null && String(data.id) === String(id?.investmentId);
  const [isPaymentStatusChanged, setIsPaymentStatusChanged] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isPdfMode, setIsPdfMode] = useState(false); // Add this state

  // What a submission in flight checks after each await (see above).
  const mountedRef = useRef(true);
  const invoiceRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  const routeIdRef = useRef(id?.investmentId);
  routeIdRef.current = id?.investmentId;
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  /**
   * -> live(): true while this page is mounted and still shows order
   * `orderId` as the route's order. Checked before every side effect of a
   * submission that has awaited something.
   */
  const submissionFor = (orderId) => () =>
    mountedRef.current &&
    orderId !== undefined &&
    orderId !== null &&
    String(routeIdRef.current) === String(orderId) &&
    !!dataRef.current &&
    String(dataRef.current.id) === String(orderId);

  var token = localStorage.getItem("accessToken");
  const formattedId = `OR${String(data?.id || "").padStart(6, "0")}`;
  const equitymessage = () => {
    const eqval1 = data?.amount;
    const eqval2 = data?.campaign_detail?.price_per_security;
    if (eqval1) {
      const percentage = eqval1 / eqval2;
      setShare(
        `Purchasing shares of  ${
          percentage == "Infinity" ? "N/A" : Math.round(percentage)
        } `
      );
    } else {
      setShare("");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  /**
   * POST admin/wire/mark-paid, awaited. -> { settled, text } when the credit
   * settled the order, or null when it did not (the reason was shown) or when
   * the page is no longer this submission's (live() false: nothing shown).
   */
  const recordCredit = async (live) => {
    let response;
    try {
      response = await axios.post(
        process.env.REACT_APP_API + "admin/wire/mark-paid",
        {
          investmentId: data?.id,
          bankReference,
          receivedAmount,
          receivedCurrency,
        },
        { headers: { "x-access-token": token } }
      );
    } catch (error) {
      if (!live()) return null;
      const body = error?.response?.data;
      const refusal = describeRefusal(body, "record");
      const code = body && (body.code || body.error);
      await Swal.fire({
        title: refusal.title,
        text:
          code === "NOT_WIRE_ROUTED"
            ? `${refusal.text} Record a manual bank credit for this order from the AD payment console.`
            : refusal.text,
        icon: "error",
        confirmButtonText: "OK",
      });
      if (refusal.reload && live()) fetchjobData();
      return null;
    }
    if (!live()) return null;
    const order = { amount: data?.amount, currency: data?.currency };
    const outcome = markPaidOutcome(response, order);
    if (!outcome.settled) {
      const s = mismatchSummary(mismatchFields(response?.data, order));
      await Swal.fire({
        title: outcome.title,
        text: `Ordered ${s.ordered}, the bank credited ${s.credited}. ${outcome.text}`,
        icon: outcome.icon,
        confirmButtonText: "OK",
      });
      if (!live()) return null;
      setPaymentStatus(data?.paymentStatus || "UNPAID");
      setIsPaymentStatusChanged(false);
      fetchjobData();
      return null;
    }
    return outcome;
  };

  const onSubmit = async () => {
    // The order loaded is not the route's (another order's answer, or none
    // yet): record nothing and send nothing from this page.
    if (!detailIsRoute) {
      Swal.fire({
        title: "Order not loaded",
        text: "The order on this page is not the one the address names, so nothing was recorded or sent. Reload the page and try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    // ✅ Check condition before proceeding
    // I1: the gate follows the RESOLVED kind. An order whose kind is not
    // resolved is refused first and outright (fail closed): which account
    // pays, and who the invoice is addressed to, are not known, and a
    // verified company does not change that.
    if (!kind) {
      Swal.fire({
        title: "Account kind not resolved",
        text: `This order's account kind is not resolved (${accountKindReasonText(resolved.reason)}), so no payment can be recorded and no invoice sent for it here.`,
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    // D-5: an ENTITY order not yet confirmed needs its company verified
    // (strictly, fail closed); a confirmed ENTITY order and an INDIVIDUAL
    // order do not. See COMPANY_NOT_STATED_DIALOG above.
    if (kind === "ENTITY" && !orderConfirmed(data)) {
      const company = companyCheckOf(data);
      if (company !== COMPANY_CHECK.VERIFIED) {
        const dialog = company === COMPANY_CHECK.NOT_STATED ? COMPANY_NOT_STATED_DIALOG : COMPANY_NOT_VERIFIED_DIALOG;
        Swal.fire({
          title: dialog.title,
          text: dialog.text,
          icon: "error",
          confirmButtonText: "OK",
        });
        return; // Stop the execution here
      }
    }
    // Amendment A3/A5: a credit is posted only when the server said whether
    // the sender check applies, and a required one is on record as a match.
    if (data?.paymentStatus !== "PAID") {
      const remitterBlock = remitterCreditBlock(data, kind);
      if (remitterBlock) {
        Swal.fire({
          title: remitterBlock.title,
          text: remitterBlock.text,
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
    }
    // Bound to this page and this order: every step after an await checks it.
    const live = submissionFor(data.id);
    setLoading(true);
    try {
      // 1. The payment, through the wire receipt rules (not for an order
      //    that is already PAID: that only re-sends its invoice).
      let settled = null;
      if (data?.paymentStatus !== "PAID") {
        settled = await recordCredit(live);
        if (!settled) return;
      }
      if (!live()) return;
      const paidNote = settled ? "The order is marked paid. " : "";

      // 2. The invoice, only for a PAID order.
      const pdfUrl = await generatePdfAndUpload(live);
      if (!live()) return;
      if (!pdfUrl) {
        await Swal.fire({
          title: settled ? "Marked paid, invoice not sent" : "Error",
          text: `${paidNote}The invoice PDF could not be generated or uploaded, so it was not sent. Try again.`,
          icon: "error",
          confirmButtonText: "OK",
        });
        if (settled && live()) fetchjobData();
        return;
      }
      let response;
      try {
        response = await axios.put(
          process.env.REACT_APP_API + "admin/update-investment",
          { id: data?.id, invoice_pdf_url: pdfUrl },
          { headers: { "x-access-token": token } }
        );
      } catch (error) {
        if (!live()) return;
        await Swal.fire({
          title: settled ? "Marked paid, invoice not sent" : "Invoice not sent",
          text: `${paidNote}The invoice was not sent: ${
            error?.response?.data?.message || "the server did not answer."
          }`,
          icon: "error",
          confirmButtonText: "OK",
        });
        if (settled && live()) fetchjobData();
        return;
      }
      if (!live()) return;
      if (response?.data?.status !== 200) {
        await Swal.fire({
          title: settled ? "Marked paid, invoice not sent" : "Invoice not sent",
          text: `${paidNote}The invoice was not sent: ${
            response?.data?.message || "unexpected answer from the server."
          }`,
          icon: "error",
          confirmButtonText: "OK",
        });
        if (settled && live()) fetchjobData();
        return;
      }
      Swal.fire({
        title: "Success",
        timer: 2500,
        timerProgressBar: true,
        text: settled
          ? `${settled.text} The invoice was sent to the investor.`
          : "The invoice was sent to the investor.",
        icon: "success",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
      navigate("/investment");
    } catch (error) {
      // Nothing is shown on a page that is no longer this order's.
      if (!live()) return;
      // Never silent: the payment may or may not have been recorded.
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Reload the order to see whether the payment was recorded before trying again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      fetchjobData();
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const fetchjobData = () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "admin/get-myinvestment-details-by-id",
      headers: {
        "x-access-token": token,
      },
      data: {
        id: id.investmentId,
      },
    };

    axios(config)
      .then(function (response) {
        // A page that is gone (the route names another order) keeps nothing.
        if (!mountedRef.current) return;
        setLoading(false);
        const fetchedData = response.data.data.rows[0];
        setData(fetchedData);
        setPaymentStatus(fetchedData.paymentStatus);
        setIsPaymentStatusChanged(false);
      })
      .catch(function (error) {
        if (!mountedRef.current) return;
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  };

  useEffect(() => {
    fetchjobData();
  }, []);

  useEffect(() => {
    if (data) {
      equitymessage();
    }
  }, [data]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  // const generatePdfAndUpload = async () => {
  //   try {
  //     const section = document.querySelector(".order-confirmation");
  //     if (!section) {
  //       console.error("No element with the class 'order-confirmation' found.");
  //       return null;
  //     }

  //     const canvas = await html2canvas(section, {
  //       scale: 1.5,
  //       useCORS: true,
  //     });

  //     const imgData = canvas.toDataURL("image/jpeg", 0.7);
  //     const pdf = new jsPDF("p", "mm", "a4");
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const imgHeight = (canvas.height * pdfWidth) / canvas.width;

  //     let position = 0;
  //     pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);

  //     let heightLeft = imgHeight - pdf.internal.pageSize.getHeight();
  //     while (heightLeft > 0) {
  //       position -= pdf.internal.pageSize.getHeight();
  //       pdf.addPage();
  //       pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
  //       heightLeft -= pdf.internal.pageSize.getHeight();
  //     }

  //     const pdfBlob = pdf.output("blob");

  //     const formData = new FormData();
  //     formData.append("media", pdfBlob, "order_confirmation.pdf");

  //     const response = await axios.post(
  //       process.env.REACT_APP_API + "utils/upload-file",
  //       formData,
  //       {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //           "x-access-token": token,
  //         },
  //       }
  //     );

  //     if (response.status === 200 && response.data?.document_name) {
  //       console.log("PDF uploaded successfully:", response.data.document_name);
  //       return response.data.document_name; // Return the uploaded PDF URL
  //     } else {
  //       console.error("Failed to upload PDF. Response:", response.data);
  //       return null;
  //     }
  //   } catch (error) {
  //     console.error("Error generating or uploading PDF:", error);
  //     return null;
  //   }
  // };

  /**
   * The invoice of this page's order as a PDF, uploaded. -> its URL, or null.
   * Captured from this page's own invoice element (invoiceRef), never from a
   * document query, and abandoned (null, nothing uploaded) as soon as live()
   * says the page is no longer the submission's.
   */
  const generatePdfAndUpload = async (live) => {
    try {
      setIsPdfMode(true);
      setIsGeneratingPDF(true);

      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!live()) return null;

      const section = invoiceRef.current;
      if (!section) {
        console.error("The invoice of this page is not rendered.");
        return null;
      }

      const canvas = await html2canvas(section, { scale: 1.5, useCORS: true });
      if (!live()) return null;
      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);

      let heightLeft = imgHeight - pdf.internal.pageSize.getHeight();
      while (heightLeft > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      const pdfBlob = pdf.output("blob");
      const formData = new FormData();
      formData.append("media", pdfBlob, "order_confirmation.pdf");

      const response = await axios.post(
        process.env.REACT_APP_API + "admin/utils/upload-file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": token,
          },
        }
      );
      if (!live()) return null;

      if (response.status === 200 && response.data?.document_name) {
        console.log("PDF uploaded successfully:", response.data.document_name);
        return response.data.document_name;
      } else {
        console.error("Failed to upload PDF. Response:", response.data);
        return null;
      }
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
      return null;
    } finally {
      if (mountedRef.current) {
        setIsPdfMode(false);
        setIsGeneratingPDF(false);
      }
    }
  };

  const handleExportExcel = async () => {
    const token = localStorage.getItem("accessToken");
    var config = {
      method: "post",
      url: process.env.REACT_APP_API + "reports/transaction",
      headers: {
        "x-access-token": token,
      },
      data: {
        investmentId: data?.id,
      },
    };

    axios(config)
      .then(function (response) {
        if (response) {
          setLoading(false);
          var abc = base64ToBlob(response.data, "application/vnd.ms-excel");
          const url = window.URL.createObjectURL(abc);
          const link = document.createElement("a");
          link.href = url;
          const filename = getDateFormat(new Date()) + "_" + data?.id;
          link.setAttribute("download", `${filename}.xlsx`);
          document.body.appendChild(link);
          link.click();
        }
      })
      .catch(function (error) {
        setLoading(false);
        console.error("Export transaction error:", error);
      });
  };

  const exportCorporate = () => {
    const data1 = Array.isArray(data) ? data : [data];
    const dataToExport = data1.map((campaign) => ({
      ID: campaign?.campaign_detail?.id,
      // R10: series name under "Campaign Name", legal entity under its own label.
      ...identityExportColumns(campaign?.campaign_detail),
      "Official Email": campaign?.campaign_detail?.camp_email,
      "Company Address": campaign?.campaign_detail?.registered_address,
      City: campaign?.campaign_detail?.city,
      "Type of entity": campaign?.campaign_detail?.entitytype,
      "Business number": campaign?.campaign_detail?.business,
      "CRA tax account": campaign?.campaign_detail?.craax_account,
      "Date Of Incorp": campaign?.campaign_detail?.date_of_incorp,
      "How many years in business":
        campaign?.campaign_detail?.how_many_yearsin_business,
      "Corporation Name": campaign?.user?.comp__name,
      "Investment Account": campaign?.investmentAccount,
      Amount: campaign?.amount,
      Currency: campaign?.currency,
      CustomerId: campaign?.user_id,
      OrderId: campaign?.order_id,
      // I1: the company's coordinates only for an ENTITY order; an unresolved order exports no bank columns.
      ...(kind === "ENTITY"
        ? {
            "Bank Country": bankCountryLabel(
              countries,
              campaign?.user?.user_banking_details?.corporate_bank_country_id
            ),
            "Bank Name": campaign?.user?.user_banking_details?.corporate_bank_name,
            // I9: the account number and IBAN are exported as the last 4 only.
            IBAN: maskAccount(campaign?.user?.user_banking_details?.corporate_iban),
            "SWIFT Code":
              campaign?.user?.user_banking_details?.corporate_swift_code,
            "Institutional Number":
              campaign?.user?.user_banking_details?.corporate_institution_number,
            "Transit Number":
              campaign?.user?.user_banking_details?.corporate_transit_number,
            "Account Number": maskAccount(
              campaign?.user?.user_banking_details?.corporate_account_number
            ),
            "Account Holder Name":
              campaign?.user?.user_banking_details?.corporate_account_holder,
          }
        : {}),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Campaigns");
    XLSX.writeFile(workbook, "CampaignListing.xlsx");
  };

  const exportIndividual = () => {
    const data1 = Array.isArray(data) ? data : [data];
    const dataToExport = data1.map((campaign) => ({
      ID: campaign?.campaign_detail?.id,
      // R10: series name under "Campaign Name", legal entity under its own label.
      ...identityExportColumns(campaign?.campaign_detail),
      "Official Email": campaign?.campaign_detail?.camp_email,
      "Company Address": campaign?.campaign_detail?.registered_address,
      City: campaign?.campaign_detail?.city,
      "Type of entity": campaign?.campaign_detail?.entitytype,
      "Business number": campaign?.campaign_detail?.business,
      "CRA tax account": campaign?.campaign_detail?.craax_account,
      "Date Of Incorp": campaign?.campaign_detail?.date_of_incorp,
      "How many years in business":
        campaign?.campaign_detail?.how_many_yearsin_business,
      "Investment Account": campaign?.investmentAccount,
      Firstname: campaign?.user?.firstname,
      Lastname: campaign?.user?.lastname,
      Amount: campaign?.amount,
      Currency: campaign?.currency,
      CustomerId: campaign?.user_id,
      OrderId: campaign?.order_id,
      // I1: the personal coordinates only for an INDIVIDUAL order; an unresolved order exports no bank columns.
      ...(kind === "INDIVIDUAL"
        ? {
            "Bank Country": bankCountryLabel(
              countries,
              campaign?.user?.user_banking_details?.bank_country_id
            ),
            "Bank Name": campaign?.user?.user_banking_details?.bank_name,
            // I9: the account number and IBAN are exported as the last 4 only.
            IBAN: maskAccount(campaign?.user?.user_banking_details?.iban),
            "SWIFT Code": campaign?.user?.user_banking_details?.swift_code,
            "Institutional Number":
              campaign?.user?.user_banking_details?.institution_number,
            "Transit Number": campaign?.user?.user_banking_details?.transit_number,
            "Account Number": maskAccount(campaign?.user?.user_banking_details?.account_number),
            "Account Holder Name":
              campaign?.user?.user_banking_details?.account_holder,
          }
        : {}),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Campaigns");
    XLSX.writeFile(workbook, "CampaignListing.xlsx");
  };

  const getDateFormat = (date) => {
    const today = date;
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1; // Months start at 0!
    let dd = today.getDate();

    if (dd < 10) dd = "0" + dd;
    if (mm < 10) mm = "0" + mm;

    return yyyy + "" + mm + "" + dd;
  };

  const base64ToBlob = (base64Data, contentType) => {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  return (
    <div className="new">
      <Loader isLoading={loading} />
      <div className="newContainer">
        <div className="top">
          <span className="backarrow">
            <FaAngleLeft onClick={() => navigate(-1)} />
          </span>
          <h1 className="view-page-tittle">Edit Investment</h1>
        </div>
        <PageShell><PageHead title="Investment edit" /><div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Corporate Information
                  </h1>
                  <div className="exportBtn-divSec">
                    <button
                      className="custBtn"
                      id="downloadbtn"
                      onClick={
                        kind === "INDIVIDUAL"
                          ? exportIndividual
                          : exportCorporate
                      }
                    >
                      Export as Excel
                    </button>
                  </div>
                </div>

                <div className="item">
                  <div className="details profile-info">
                    <div className="detailItem">
                      <span className="itemKey">{ISSUER_LEGAL_NAME_LABEL}:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.corporation_legal_name}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Email</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.camp_email}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Company Address:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.registered_address}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">City</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.city}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Country</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.country_name || "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">State/Province</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.state_name || "-"}
                      </span>
                    </div>
                    {data?.campaign_detail?.type === "FUND" && (
                      <div className="detailItem">
                        <span className="itemKey">Issuer jurisdiction</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.issuer_jurisdiction || "-"}
                        </span>
                      </div>
                    )}

                    <div className="detailItem">
                      <span className="itemKey">Type of entity:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.entitytype}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Business number:</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.business}
                      </span>
                    </div>

                    <div className="detailItem">
                      <span className="itemKey">Fax</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.fax !== "undefined" &&
                        data?.campaign_detail?.fax !== "null"
                          ? data?.campaign_detail?.fax
                          : "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">CRA tax account</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.craax_account !== "undefined" &&
                        data?.campaign_detail?.craax_account !== "null"
                          ? data?.campaign_detail?.craax_account
                          : "-"}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Date Of Corp</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.date_of_incorp}
                      </span>
                    </div>
                    <div className="detailItem">
                      <span className="itemKey">Year Of Corporation</span>
                      <span className="itemValue">
                        {data?.campaign_detail?.how_many_yearsin_business}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div></PageShell>
        <div className="single">
          <div className="singleContainer">
            <div className="top">
              <div
                className="left"
                style={{ textAlign: "left", boxShadow: "unset" }}
              >
                <div className="topHeading">
                  {" "}
                  <h1
                    className="title"
                    style={{ fontSize: "16px", color: "#333", width: "100%" }}
                  >
                    Personal Information
                  </h1>
                </div>

                {kind === "INDIVIDUAL" ? (
                  <div className="item">
                    <div className="details profile-info">
                      <div className="detailItem">
                        <span className="itemKey">First Name</span>
                        <span className="itemValue">
                          {data?.user?.firstname}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Last Name</span>
                        <span className="itemValue">
                          {data?.user?.lastname}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Investment Account</span>
                        <span className="itemValue">
                          {data?.investmentAccount}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign ID</span>
                        <span className="itemValue">{data?.campaign_id}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">{CAMPAIGN_NAME_LABEL}</span>
                        <span className="itemValue">
                          {campaignDisplayName(data?.campaign_detail)}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">{ISSUER_LEGAL_NAME_LABEL}</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.corporation_legal_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Amount</span>
                        <span className="itemValue">{data?.amount}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Currency</span>
                        <span className="itemValue">{data?.currency}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Country</span>
                        <span className="itemValue">
                          {bankCountryLabel(
                            countries,
                            data?.user?.user_banking_details?.bank_country_id
                          )}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Bank Name</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.bank_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">IBAN</span>
                        <span className="itemValue">
                          {maskAccount(data?.user?.user_banking_details?.iban)}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">SWIFT Code</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.swift_code}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Institutional Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.institution_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Transit / Routing Number</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.transit_number}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Number</span>
                        <span className="itemValue">
                          {maskAccount(data?.user?.user_banking_details?.account_number)}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Account Holder</span>
                        <span className="itemValue">
                          {data?.user?.user_banking_details?.account_holder}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Order Number</span>
                        <span className="itemValue">{data?.order_id}</span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Payment Method</span>
                        <span className="itemValue">{data?.paymentOption}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="item">
                    <div className="details profile-info">
                      <div className="detailItem">
                        <span className="itemKey">Corporation Name</span>
                        <span className="itemValue">
                          {data?.user?.comp__name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Investment Account</span>
                        <span className="itemValue">
                          {data?.investmentAccount}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Campaign ID</span>
                        <span className="itemValue">{data?.campaign_id}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">{CAMPAIGN_NAME_LABEL}</span>
                        <span className="itemValue">
                          {campaignDisplayName(data?.campaign_detail)}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">{ISSUER_LEGAL_NAME_LABEL}</span>
                        <span className="itemValue">
                          {data?.campaign_detail?.corporation_legal_name}
                        </span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Amount</span>
                        <span className="itemValue">{data?.amount}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Currency</span>
                        <span className="itemValue">{data?.currency}</span>
                      </div>
                      {/* I1: the company's coordinates only for a resolved
                          ENTITY order; an unresolved one shows none. */}
                      {kind === "ENTITY" ? (
                        <>
                          <div className="detailItem">
                            <span className="itemKey">Bank Country</span>
                            <span className="itemValue">
                              {bankCountryLabel(
                                countries,
                                data?.user?.user_banking_details
                                  ?.corporate_bank_country_id
                              )}
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Bank Name</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_bank_name
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">IBAN</span>
                            <span className="itemValue">
                              {maskAccount(data?.user?.user_banking_details?.corporate_iban)}
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">SWIFT Code</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_swift_code
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Institutional Number</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_institution_number
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Transit / Routing Number</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_transit_number
                              }
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Account Number</span>
                            <span className="itemValue">
                              {maskAccount(
                                data?.user?.user_banking_details
                                  ?.corporate_account_number
                              )}
                            </span>
                          </div>
                          <div className="detailItem">
                            <span className="itemKey">Account Holder</span>
                            <span className="itemValue">
                              {
                                data?.user?.user_banking_details
                                  ?.corporate_account_holder
                              }
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="detailItem">
                          <span className="itemKey">Bank details</span>
                          <span className="itemValue">
                            Not shown: this order's account kind is not
                            resolved ({accountKindReasonText(resolved.reason)}).
                          </span>
                        </div>
                      )}

                      <div className="detailItem">
                        <span className="itemKey">Order Number</span>
                        <span className="itemValue">{data?.order_id}</span>
                      </div>

                      <div className="detailItem">
                        <span className="itemKey">Payment Method</span>
                        <span className="itemValue">{data?.paymentOption}</span>
                      </div>
                      <div className="detailItem">
                        <span className="itemKey">Payment Action</span>
                        <select
                          className="itemValue dropdown"
                          value={paymentStatus}
                          disabled={isClosedOrder(data) || isOpenPaidOrder(data)}
                          onChange={(e) => {
                            // A closed order takes no payment action; a paid one is read-only.
                            if (isClosedOrder(data) || isOpenPaidOrder(data)) return;
                            setPaymentStatus(e.target.value);
                            setIsPaymentStatusChanged(true);
                          }}
                          style={{ width: "200px" }}
                        >
                          <option value="PAID">PAID</option>
                          <option value="UNPAID">UNPAID</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="single">
              <div className="singleContainer">
                <div className="top">
                  <div
                    className="left"
                    style={{ textAlign: "left", boxShadow: "unset" }}
                  >
                    <div className="topHeading">
                      {" "}
                      <h1
                        className="title"
                        style={{
                          fontSize: "16px",
                          color: "#333",
                          width: "100%",
                        }}
                      >
                        Update Payment Status & Send Invoice
                      </h1>
                    </div>
                    {/* Invoive Section start */}

                    <>
                      {/* <div className="">
                        <div className="campDashboard-container negativeTopSpace investNow-section">
                          <div className="container"> */}
                      <div className="campDashboard-wrapper investNow-content">
                        <div className="order-confirmation" ref={invoiceRef}>
                          <div className="invoice-logo-container">
                            <img src={Logo} />
                          </div>
                          <div className="order-confirmation-title">
                            <div className="blue-box"></div>
                            <h1>Order Confirmation</h1>
                          </div>
                          <div className="content">
                            <h2>To</h2>
                            <section className="invoice-details">
                              {/* I1: the addressee follows the RESOLVED kind: the
                                  company for ENTITY, the person for INDIVIDUAL,
                                  and no guess for an unresolved order. */}
                              <div className="to">
                                {kind ? (
                                  <>
                                    <h5>
                                      {kind === "ENTITY"
                                        ? data?.user?.comp__name
                                        : `${data?.user?.firstname
                                            ?.charAt(0)
                                            .toUpperCase()}${data?.user?.firstname?.slice(
                                            1
                                          )}
                                                ${data?.user?.lastname
                                                  ?.charAt(0)
                                                  .toUpperCase()}${data?.user?.lastname?.slice(
                                            1
                                          )}`}
                                    </h5>
                                    <p>
                                      {console.log("add", data?.user?.firstname)}
                                      {kind === "ENTITY"
                                        ? data?.user?.comp_address
                                        : data?.user?.current_address}
                                      ,
                                      <br />
                                      {kind === "ENTITY"
                                        ? `${data?.user?.data?.city_name || ""},
                                              ${data?.user?.state_name || ""},
                                              ${data?.user?.county_name || ""},
                                              ${data?.user?.comp_postal_code || ""}`
                                        : `${data?.user?.city_name || ""},
                                              ${data?.user?.state_name || ""},
                                              ${data?.user?.county_name || ""}
                                              ${data?.user?.postal_code || ""}`}
                                    </p>
                                  </>
                                ) : (
                                  <h5>Addressee not resolved: this order's account kind is not resolved.</h5>
                                )}
                              </div>
                              <div className="invoice-info">
                                <div className="inner-content">
                                  <p>
                                    <strong>Invoice no:</strong>{" "}
                                    <span>{data?.order_id}</span>
                                  </p>
                                  <p>
                                    <strong>Date:</strong>{" "}
                                    <span>{formatDate(data?.dateCreated)}</span>
                                  </p>
                                </div>
                              </div>
                            </section>
                            <table className="order-table">
                              <thead>
                                <tr>
                                  <th>No</th>
                                  <th>Description</th>
                                  <th>Qty</th>
                                  <th>Price/Share</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{data?.order_id}</td>
                                  <td>
                                    {data?.campaign_detail?.security_type}{" "}
                                    issued by{" "}
                                    {
                                      data?.campaign_detail
                                        ?.corporation_legal_name
                                    }
                                  </td>
                                  <td>
                                    {data?.campaign_detail?.issued_shares}
                                  </td>
                                  <td>
                                    {data?.campaign_detail?.price_per_security}
                                  </td>
                                  <td>{data?.amount}</td>
                                </tr>
                              </tbody>
                            </table>
                            <hr className="divider" />
                            <section className="payment-details">
                              <div className="inner-sections">
                                <h2>Payment Method</h2>

                                <div className="checkoutdiv-body">
                                  <p>
                                    {paymentMethodLabel(data?.paymentOption)}
                                  </p>
                                </div>

                                {/* PAYMENT STATUS IS NOT A PROPERTY OF THE RAIL.
                                    This control used to be nested inside the
                                    `paymentMethod == "Wire Transfer"` branch, and
                                    that branch was only ever reachable because
                                    the state it tested was hardcoded to "Wire
                                    Transfer". Deriving the method from the order
                                    without also hoisting this would have taken
                                    the PAID/UNPAID control away from every
                                    Direct Deposit order and every order that has
                                    not reached the payment step — a worse defect
                                    than the wrong label it fixes. */}
                                <div className="checkoutdiv-body">
                                  <p>
                                    <span>
                                      <div className="detailItem">
                                        <span className="itemKey">
                                          Payment Status
                                        </span>
                                        &nbsp;
                                        {isGeneratingPDF || isPdfMode ? ( // Added `isPdfMode` for better control
                                          <span
                                            style={{
                                              color:
                                                paymentStatus === "PAID"
                                                  ? "green"
                                                  : "red",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {paymentStatus}
                                          </span>
                                        ) : (
                                          <select
                                            className="itemValue dropdown"
                                            value={paymentStatus}
                                            disabled={isClosedOrder(data) || isOpenPaidOrder(data)}
                                            onChange={(e) => {
                                              // A closed order takes no payment action; a paid one is read-only.
                                              if (isClosedOrder(data) || isOpenPaidOrder(data)) return;
                                              setPaymentStatus(e.target.value);
                                              setIsPaymentStatusChanged(true);
                                            }}
                                            style={{ width: "200px" }}
                                          >
                                            <option value="PAID">PAID</option>
                                            <option value="UNPAID">
                                              UNPAID
                                            </option>
                                          </select>
                                        )}
                                      </div>
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="inner-sections">
                                <h2>Instructions</h2>
                                <p>
                                  Payment has been received, and you will
                                  receive confirmation within 2 working days. In
                                  case of any issues, please contact{" "}
                                  <i>
                                    <a href="mailto:payments@rootsfundme.ca">
                                      payments@rootsfundme.ca
                                    </a>
                                  </i>
                                </p>
                              </div>
                            </section>
                            <div className="contact-section">
                              <hr className="divider" />
                              <div className="contact-details">
                                <div className="contact-item">
                                  <i className="fa fa-envelope icon"></i>
                                  <div className="contact-text">
                                    <span className="label">Mail</span>
                                    <a
                                      href="mailto:management@rootsfundme.ca"
                                      className="value"
                                    >
                                      management@rootsfundme.ca
                                    </a>
                                  </div>
                                </div>
                                <div className="contact-item">
                                  <i className="fa fa-globe icon"></i>
                                  <div className="contact-text">
                                    <span className="label">Website</span>
                                    <a
                                      href="http://www.rootsfundme.ca"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="value"
                                    >
                                      www.rootsfundme.ca
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {isClosedOrder(data) && (
                          // Outside .order-confirmation: never painted into
                          // the invoice PDF.
                          <p
                            className="closed-order-notice"
                            role="alert"
                            style={{ maxWidth: "520px", margin: "24px auto 0", textAlign: "left", fontSize: "14px" }}
                          >
                            {CLOSED_ORDER_TEXT}
                          </p>
                        )}
                        {isPaymentStatusChanged &&
                          paymentStatus === "PAID" &&
                          data?.paymentStatus !== "PAID" && (
                            // Outside .order-confirmation: never painted into
                            // the invoice PDF.
                            <div
                              className="mark-paid-credit"
                              style={{ maxWidth: "520px", margin: "24px auto 0", textAlign: "left" }}
                            >
                              <p style={{ fontSize: "14px" }}>
                                Record the bank credit exactly as the statement shows it. The order is
                                marked paid only when the credit matches it exactly (AD only).
                              </p>
                              <label htmlFor="mark-paid-bank-reference" style={{ display: "block", marginTop: "8px" }}>
                                Bank reference of the incoming payment
                              </label>
                              <input
                                id="mark-paid-bank-reference"
                                className="form-control"
                                value={bankReference}
                                onChange={(e) => setBankReference(e.target.value)}
                                placeholder="e.g. FT26100700123"
                              />
                              <label htmlFor="mark-paid-received-amount" style={{ display: "block", marginTop: "8px" }}>
                                Amount the bank actually credited
                              </label>
                              <input
                                id="mark-paid-received-amount"
                                className="form-control"
                                inputMode="decimal"
                                value={receivedAmount}
                                onChange={(e) => setReceivedAmount(e.target.value)}
                                placeholder="Plain number, no separators"
                              />
                              <label htmlFor="mark-paid-received-currency" style={{ display: "block", marginTop: "8px" }}>
                                Currency the bank actually credited
                              </label>
                              <select
                                id="mark-paid-received-currency"
                                className="form-control"
                                value={receivedCurrency}
                                onChange={(e) => setReceivedCurrency(e.target.value)}
                              >
                                <option value="">Select a currency</option>
                                {Array.from(
                                  new Set([...CREDIT_CURRENCIES, data?.currency].filter(Boolean))
                                ).map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        {(isPaymentStatusChanged || isOpenPaidOrder(data)) && (
                          <div className="my-5 d-flex justify-content-center">
                            <button
                              type="button"
                              className="custom-btn-outline text-white bg-success"
                              style={{
                                fontSize: "16px",
                                color: "#fff",
                                marginTop: "20px",
                                borderRadius: "15px",
                                width: "10%",
                                marginLeft: "35%",
                                marginRight: "5%",
                                padding: "10px",
                                background: "#4fbd88",
                                alignSelf: "center",
                                minWidth: "150px",
                                textAlign: "center",
                              }}
                              onClick={() => {
                                if (paymentStatus !== "UNPAID") {
                                  setPaymentStatus(paymentStatus);
                                  onSubmit();
                                }
                              }}
                            >
                              {isOpenPaidOrder(data) ? "Send invoice" : "Continue"}
                            </button>
                            <button
                              type="button"
                              className="custom-btn-outline text-white"
                              style={{
                                fontSize: "16px",
                                color: "#fff",
                                marginTop: "10px",
                                borderRadius: "15px",
                                width: "10%",
                                padding: "10px",
                                background: "#dc3545",
                                minWidth: "150px",
                                textAlign: "center",
                              }}
                              onClick={() => window.history.back()}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </>

                    {/* Invoive Section end */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Section */}
    </div>
  );
};

/**
 * One page per order. The detail is fetched on mount, so a hash-route change
 * from order A to order B remounts the page: nothing read for A (its detail,
 * its payment status, the credit typed for it) survives on B's page.
 */
const InvestmentEditRoute = () => {
  const { investmentId } = useParams();
  return <Viewinvestment key={investmentId} />;
};

export default InvestmentEditRoute;
